import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { inspect, log } from '@/utils/logger'
import { FreeCoffeeType, FreeStuffEvaluationJobInfo, FreeStuffPolicyCategory, FreeStuffIneligibilityReason } from 'aven_types'
import { getFreeCoffeeSelectionByPolicy, getFreeStuffEvaluationJobInfo } from '@/services/api'
import { getFreeCoffeeAssetsAndCopy } from '../utils/advisorV2/freeCoffeeCardUtils'

export const useFreeCoffeeCardStore = defineStore('freeCoffeeCard', () => {
    // State
    const freeCoffeeSelectionByPolicy = ref<{ freeCoffeeType: FreeCoffeeType | null } | null>(null)
    const freeStuffEvaluationJobInfo = ref<FreeStuffEvaluationJobInfo | null>(null)

    watch(freeCoffeeSelectionByPolicy, (oldVal, newVal) => {
        log.info('freeCoffeeSelectionByPolicy => oldVal: %{oldVal}, newVal: %{newVal}', { oldVal: inspect(oldVal), newVal: inspect(newVal) })
    })
    watch(freeStuffEvaluationJobInfo, (oldVal, newVal) => {
        log.info('freeStuffEvaluationJobInfo => oldVal: %{oldVal}, newVal: %{newVal}', { oldVal: inspect(oldVal), newVal: inspect(newVal) })
    })

    // Computed
    const freeCoffeeType = computed<FreeCoffeeType | null>(() => {
        return freeCoffeeSelectionByPolicy.value?.freeCoffeeType ?? null
    })

    const freeStuffInfo = computed(() => {
        return freeStuffEvaluationJobInfo.value?.freeStuffInfo ?? null
    })

    const ineligibilityReasons = computed<FreeStuffIneligibilityReason[]>(() => {
        return freeStuffInfo.value?.ineligibilityReasons ?? []
    })

    const freeCoffeeAssetsAndCopy = computed(() => {
        const coffeeType = freeCoffeeType.value
        if (!coffeeType) {
            log.info('freeCoffeeAssetsAndCopy => No freeCoffeeType found, returning null')
            return null
        }
        const stuffInfo = freeStuffInfo.value
        log.info('freeCoffeeAssetsAndCopy => freeCoffeeType: %{coffeeType}, freeStuffInfo: %{stuffInfo}', { coffeeType, stuffInfo: inspect(stuffInfo) })
        return getFreeCoffeeAssetsAndCopy(coffeeType, stuffInfo)
    })

    // Actions
    async function fetchFreeCoffeeData(force: boolean = false): Promise<void> {
        log.info('fetchFreeCoffeeData => Fetching free coffee data')
        await Promise.all([maybeFetchFreeCoffeeSelectionByPolicy(force), maybeGetFreeStuffEvaluationJobInfo(FreeStuffPolicyCategory.FREE_COFFEE, force)])
        log.info('fetchFreeCoffeeData => Successfully fetched free coffee data')
        log.info('fetchFreeCoffeeData => freeCoffeeSelectionByPolicy: %{freeCoffeeSelectionByPolicy}', { freeCoffeeSelectionByPolicy: inspect(freeCoffeeSelectionByPolicy.value) })
        log.info('fetchFreeCoffeeData => freeCoffeeType: %{freeCoffeeType}', { freeCoffeeType: freeCoffeeSelectionByPolicy.value?.freeCoffeeType ?? 'null' })
        log.info('fetchFreeCoffeeData => freeStuffEvaluationJobInfo: %{freeStuffEvaluationJobInfo}', { freeStuffEvaluationJobInfo: inspect(freeStuffEvaluationJobInfo.value) })
        log.info('fetchFreeCoffeeData => freeCoffeeAssetsAndCopy computed: %{freeCoffeeAssetsAndCopy}', { freeCoffeeAssetsAndCopy: freeCoffeeAssetsAndCopy.value ? 'HAS DATA' : 'NULL' })
    }

    // This allows us to immediately show some free coffee assets and copy while we wait for the free stuff evaluation job info to be fetched
    async function maybeFetchFreeCoffeeSelectionByPolicy(force: boolean = false): Promise<{ freeCoffeeType: FreeCoffeeType | null } | null> {
        // Only skip fetching if we have a valid freeCoffeeType (not null or undefined)
        if (freeCoffeeSelectionByPolicy.value?.freeCoffeeType && !force) {
            log.info('maybeFetchFreeCoffeeSelectionByPolicy => Already has freeCoffeeSelectionByPolicy with freeCoffeeType, not refetching')
            return freeCoffeeSelectionByPolicy.value
        }

        try {
            const response = await getFreeCoffeeSelectionByPolicy()
            if (!response?.data?.success || !response?.data?.payload) {
                log.warn('maybeFetchFreeCoffeeSelectionByPolicy => Unable to fetch freeCoffeeSelectionByPolicy')
                freeCoffeeSelectionByPolicy.value = null
                return null
            }
            log.info('maybeFetchFreeCoffeeSelectionByPolicy => Successfully retrieved freeCoffeeSelectionByPolicy %{payload}', { payload: JSON.stringify(response.data.payload) })

            freeCoffeeSelectionByPolicy.value = response.data.payload

            log.info('maybeFetchFreeCoffeeSelectionByPolicy => Successfully set freeCoffeeSelectionByPolicy %{freeCoffeeSelectionByPolicy}', {
                freeCoffeeSelectionByPolicy: JSON.stringify(freeCoffeeSelectionByPolicy.value),
            })
            return response.data.payload
        } catch (err: any) {
            log.error('maybeFetchFreeCoffeeSelectionByPolicy => Error fetching freeCoffeeSelectionByPolicy', { error: err })
            freeCoffeeSelectionByPolicy.value = null
            return null
        }
    }

    async function maybeGetFreeStuffEvaluationJobInfo(freeStuffPolicyCategory: FreeStuffPolicyCategory, force: boolean = false): Promise<FreeStuffEvaluationJobInfo> {
        // Get a new jobId to poll for if force is true OR if we don't have a jobId (freeStuffEvaluationJobInfo === null)
        let jobInfo = freeStuffEvaluationJobInfo.value
        if (force || jobInfo === null) {
            jobInfo = await getAndStoreFreeStuffEvaluationJobInfo(freeStuffPolicyCategory)
        }

        // Poll until we get a terminal freeStuffEvaluationJobInfo
        if (!jobInfo.continuePolling) {
            return jobInfo
        } else {
            return pollForTerminalFreeStuffEvaluationJobInfo(freeStuffPolicyCategory, jobInfo.jobId)
        }
    }

    async function getAndStoreFreeStuffEvaluationJobInfo(freeStuffPolicyCategory: FreeStuffPolicyCategory, jobId?: string): Promise<FreeStuffEvaluationJobInfo> {
        const freeStuffEvaluationJobInfoResponse = await getFreeStuffEvaluationJobInfo(freeStuffPolicyCategory, jobId)
        const jobInfo = freeStuffEvaluationJobInfoResponse.data.payload
        freeStuffEvaluationJobInfo.value = jobInfo
        return jobInfo
    }

    async function pollForTerminalFreeStuffEvaluationJobInfo(freeStuffPolicyCategory: FreeStuffPolicyCategory, jobId: string): Promise<FreeStuffEvaluationJobInfo> {
        const MAX_NUMBER_OF_POLLING_ATTEMPTS_PER_CALL = 50
        const DELAY_BETWEEN_POLLING_ATTEMPTS_MS = 3000
        log.info('pollForTerminalFreeStuffEvaluationJobInfo => Starting poll for free stuff evaluation job info with jobId: %{jobId}', { jobId })

        for (let numberOfAttempts = 0; numberOfAttempts < MAX_NUMBER_OF_POLLING_ATTEMPTS_PER_CALL; numberOfAttempts++) {
            log.info('pollForTerminalFreeStuffEvaluationJobInfo => Polling attempt %{numberOfAttempts}', { numberOfAttempts })
            const jobInfo = await getAndStoreFreeStuffEvaluationJobInfo(freeStuffPolicyCategory, jobId)
            log.info('pollForTerminalFreeStuffEvaluationJobInfo => Attempt %{numberOfAttempts} - continuePolling: %{continuePolling}, terminalState: %{terminalState}', {
                numberOfAttempts,
                continuePolling: jobInfo?.continuePolling,
                terminalState: jobInfo?.freeStuffInfo?.terminalState,
            })

            if (jobInfo && jobInfo.continuePolling === false) {
                if (jobInfo.freeStuffInfo && jobInfo.freeStuffInfo.terminalState === false && jobInfo.continuePolling === false) {
                    throw new Error('should not have continuePolling == false if terminalState == false')
                }
                log.info('pollForTerminalFreeStuffEvaluationJobInfo => Free stuff evaluation job info indicates no more polling needed, stopping poll and updating store')
                freeStuffEvaluationJobInfo.value = jobInfo
                return jobInfo
            }

            // Don't wait after last attempt
            if (numberOfAttempts < MAX_NUMBER_OF_POLLING_ATTEMPTS_PER_CALL - 1) {
                await new Promise((resolve) => setTimeout(resolve, DELAY_BETWEEN_POLLING_ATTEMPTS_MS))
            } else {
                log.warn('pollForTerminalFreeStuffEvaluationJobInfo => Reached max attempts (%{maxAttempts}), returning last jobInfo with continuePolling: %{continuePolling}', {
                    maxAttempts: MAX_NUMBER_OF_POLLING_ATTEMPTS_PER_CALL,
                    continuePolling: jobInfo?.continuePolling,
                })
                return jobInfo
            }
        }

        // This should never be reached, but TypeScript needs it
        throw new Error('pollForTerminalFreeStuffEvaluationJobInfo => Unexpected: exited loop without returning')
    }

    function reset(): void {
        freeCoffeeSelectionByPolicy.value = null
        freeStuffEvaluationJobInfo.value = null
    }

    return {
        // Computed
        freeCoffeeAssetsAndCopy,
        freeCoffeeType,
        freeStuffInfo,
        ineligibilityReasons,

        // Actions
        fetchFreeCoffeeData,
        reset,
    }
})
